import React, { useEffect, useState, useCallback, useMemo } from "react";
import axiosInstance from "../../../../api/axiosInstance";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import { Search, ArrowRight, Layers, ChevronLeft, ChevronRight, Home } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const AllServicesPage = () => {
  const location = useLocation();
  const initialQ = new URLSearchParams(location.search).get("q") || "";

  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState(initialQ);
  const [isLoading, setIsLoading] = useState(true);
  
  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchServices = useCallback(
    async (term = "", page = 1) => {
      setIsLoading(true);
      try {
        const res = await axiosInstance.get(`/users/services`, {
          params: { q: term, page: page, limit: limit },
          headers: { Authorization: `Bearer ${token}` },
        });

        const list = res.data?.services || [];
        const pagination = res.data?.pagination || {};

        const formatted = list.map((srv) => ({
          ...srv,
          image: srv.image || srv.thumbnailUrl || null,
        }));

        setServices(formatted);
        setTotalPages(pagination.pages || 1);
        setCurrentPage(pagination.page || 1);
      } catch (err) {
        console.error("❌ Error fetching services:", err);
        setServices([]);
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const debouncedFetch = useMemo(() => debounce((term, page) => fetchServices(term, page), 400), [fetchServices]);

  useEffect(() => {
    debouncedFetch(searchTerm, currentPage);
    return () => debouncedFetch.cancel();
  }, [searchTerm, currentPage, debouncedFetch]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-outfit">
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-28 px-6 bg-[#020617] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover" alt="bg" />
        </div>
        <div className="container mx-auto relative z-10 max-w-4xl">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-8 justify-center">
            <Link to="/home" className="hover:text-white transition-colors flex items-center gap-1">
              <Home size={14} /> Home
            </Link>
            <ChevronRight size={14} className="opacity-50" />
            <span className="text-white">Services</span>
          </nav>

          <div className="text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-4xl font-semibold text-white mb-8 leading-tight">
              Discover <span className="text-blue-400">Professional</span> Services
            </motion.h1>
            
            <div className="max-w-2xl mx-auto relative group">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Search services (e.g., Legal, Audit)..." 
                value={searchTerm} 
                onChange={handleSearchChange}
                className="w-full pl-12 pr-6 py-3.5 bg-white rounded-xl shadow-lg outline-none text-base border-2 border-transparent focus:border-[#0097b2] transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="container mx-auto px-6 -mt-12 pb-20 relative z-20 max-w-7xl">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => <div key={i} className="h-72 bg-white animate-pulse rounded-3xl" />)}
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl shadow-lg border border-slate-100">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">No services found</h3>
            <p className="text-slate-500 mt-2">Try adjusting your search query.</p>
            <button onClick={() => { setSearchTerm(""); setCurrentPage(1); }} className="mt-6 text-[#134DA7] font-bold hover:underline">Clear Search</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <AnimatePresence>
              {services.map((service, index) => (
                <motion.div 
                  key={service._id} 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  onClick={() => navigate(`/services/${service._id}`)}
                  className="group bg-white p-3 rounded-[2rem] shadow-sm hover:shadow-xl transition-all border border-slate-100 cursor-pointer flex flex-col"
                >
                  <div className="h-44 rounded-[1.5rem] overflow-hidden mb-4 bg-slate-100 relative shrink-0">
                    {service.image ? (
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/400x300?text=Service"; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Layers className="w-10 h-10 text-slate-300" /></div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#134DA7] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-md">Popular</span>
                    </div>
                  </div>
                  
                  <div className="px-2 pb-2 flex flex-col flex-grow">
                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#134DA7] transition-colors line-clamp-1">{service.title}</h3>
                    <p className="text-slate-500 text-xs line-clamp-2 mt-1 mb-4">{service.description || "Expert professional service tailored to your specific business needs and goals."}</p>
                    
                    <div className="mt-auto pt-4 border-t flex justify-between items-center">
                      <span className="text-xs font-semibold text-[#134DA7] uppercase tracking-wider">
                        Explore
                      </span>
                      <ArrowRight size={16} className="text-slate-300 group-hover:text-[#134DA7] transform transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* --- Pagination Controls --- */}
        {!isLoading && services.length > 0 && totalPages > 1 && (
          <div className="flex justify-center items-center mt-12 gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${
                  currentPage === i + 1
                    ? "bg-[#134DA7] text-white shadow-lg shadow-blue-200"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default AllServicesPage;